import { ActionModal, colors } from '@insurancemenu/imenu-components-lib';
import { InsertChart, Mail } from '@insurancemenu/imenu-components-lib/dist/icons';
import { observer } from 'mobx-react-lite';
import React from 'react';
import { useTriggerRenewalsPageStore } from 'store/pages/tools/trigger-renewals/pageStore';
import { ModalType } from '@insurancemenu/imenu-components-lib/dist/helpers/ModalsHelper';
import PeriodsModalTable from './PeriodsModalTable';
import { ModalNoteStyled, ModalNotesStyled, ReloadIconStyled } from './styles';

const WarningModals: React.FC = observer(() => {
  const { batchActionDelegate, renewalGroupDelegates } = useTriggerRenewalsPageStore();
  const {
    isWarningModalOpen,
    warningModalType,
    isSelectAllButtonChecked,
    selectedPeriods,
    selectedGroupIds,
    closeWarningModal,
    sendRenewalDigest,
    triggerAllRenewal,
    moveAllToRenewal,
    deleteSelectedEnrollments,
    triggerSelectedRenewals,
    moveSelectedPeriodsToRenewals,
  } = batchActionDelegate;

  const selectedGroups = selectedGroupIds.map((groupId) => ({
    id: groupId,
    name: renewalGroupDelegates.find((delegate) => delegate.group.id === groupId)?.group.name ?? '',
  }));

  const emailsWarningMessage = (
    <span>
      <ModalNoteStyled>Note:</ModalNoteStyled> Emails send in a queue, so to receive actual list, pls reload page in
      couple of minutes.
    </span>
  );

  const selectedPeriodsTable = isSelectAllButtonChecked ? undefined : (
    <PeriodsModalTable periods={selectedPeriods} groups={selectedGroups} />
  );

  switch (warningModalType) {
    case 'SEND_STATISTICS':
      return (
        <ActionModal
          show={isWarningModalOpen}
          onClose={closeWarningModal}
          onAction={async (): Promise<void> => {
            await sendRenewalDigest();
            closeWarningModal();
          }}
          actionButtonText="Send Statistics"
          title="Please Confirm an Action"
          icon={<InsertChart width="5.6rem" color={colors.green[600]} />}
        />
      );
    case 'TRIGGER_ALL':
      return (
        <ActionModal
          show={isWarningModalOpen}
          onClose={closeWarningModal}
          onAction={async (): Promise<void> => {
            closeWarningModal();
            await triggerAllRenewal();
          }}
          title="Please Confirm an Action"
          subtitle={
            <ModalNotesStyled>
              <span>This action will move all periods to renewal and send emails.</span>
              {emailsWarningMessage}
            </ModalNotesStyled>
          }
          actionButtonText="Trigger All"
          icon={<Mail width="5.6rem" color={colors.green[600]} />}
        />
      );
    case 'TRIGGER_SELECTED':
      return (
        <ActionModal
          show={isWarningModalOpen}
          onClose={closeWarningModal}
          onAction={async (): Promise<void> => {
            closeWarningModal();
            await triggerSelectedRenewals();
          }}
          title="Please Confirm an Action"
          subtitle={
            <ModalNotesStyled>
              <span>This action will move periods to renewal and send emails.</span>
              {emailsWarningMessage}
            </ModalNotesStyled>
          }
          actionButtonText="Trigger"
          icon={<Mail width="5.6rem" color={colors.green[600]} />}
          modalContent={selectedPeriodsTable}
        />
      );
    case 'MOVE_ALL_TO_RENEWAL':
      return (
        <ActionModal
          show={isWarningModalOpen}
          onClose={closeWarningModal}
          onAction={async (): Promise<void> => {
            closeWarningModal();
            await moveAllToRenewal();
          }}
          title="Please Confirm an Action"
          subtitle={
            <ModalNotesStyled>
              <span>This action will move the all periods to renewal without sending emails.</span>
              {emailsWarningMessage}
            </ModalNotesStyled>
          }
          actionButtonText="Move All to Renewal"
          icon={<ReloadIconStyled width="5.6rem" color={colors.green[600]} />}
        />
      );
    case 'MOVE_SELECTED_PERIODS_TO_RENEWAL':
      return (
        <ActionModal
          show={isWarningModalOpen}
          onClose={closeWarningModal}
          onAction={async (): Promise<void> => {
            closeWarningModal();
            await moveSelectedPeriodsToRenewals();
          }}
          title="Please Confirm an Action"
          actionButtonText="Move to Renewal"
          subtitle={
            <ModalNotesStyled>
              <span>This action will move selected periods to renewal without sending emails.</span>
              {emailsWarningMessage}
            </ModalNotesStyled>
          }
          icon={<ReloadIconStyled width="5.6rem" color={colors.green[600]} />}
          modalContent={selectedPeriodsTable}
        />
      );
    case 'REMOVE_PERIODS':
      return (
        <ActionModal
          show={isWarningModalOpen}
          onClose={closeWarningModal}
          onAction={async (): Promise<void> => {
            closeWarningModal();
            await deleteSelectedEnrollments();
          }}
          title="Please Confirm an Action"
          subtitle={`This action will remove ${selectedPeriods.length} selected periods. You will no longer be able to make any changes for these periods.`}
          actionButtonText="Remove periods"
          modalType={ModalType.DELETE}
          modalContent={selectedPeriodsTable}
        />
      );
    default:
      return null;
  }
});

export default WarningModals;
